/**
 * TrumpApprovalMap Component
 * 
 * Interactive US map visualization showing Trump job approval ratings by state.
 * This is the primary visualization component of the application.
 * 
 * Features:
 * - Choropleth map with color-coded approval ratings
 * - Interactive state selection with click handlers
 * - Responsive design that adapts to sidebar changes
 * - Dark/light theme support
 * - Detailed hover tooltips with approval/disapproval/no-opinion rates
 * - Fixed color scale (0-100%) for consistent comparison
 * - Multi-language support
 * 
 * Data Flow:
 * 1. Receives filtered state data from parent App component
 * 2. Transforms data into Plotly-compatible format
 * 3. Renders choropleth map with approval rates as colors
 * 4. Handles user interactions (clicks, hovers)
 * 5. Communicates state selections back to parent
 * 
 * Technical Details:
 * - Uses Plotly.js for map rendering and interactions
 * - Implements responsive behavior with plot revision system
 * - Handles sidebar animations with debounced resize events
 * - Color scale: Red (low approval) to Blue (high approval)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import {
  Box, Typography, useTheme, IconButton, Tooltip
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from '../utils/translations';

// Approximate centroids (lon, lat) for Plotly geo.center & label placement
const STATE_CENTROIDS = {
  AL:{lon:-86.8,lat:32.8}, AK:{lon:-152.4,lat:64.8}, AZ:{lon:-111.7,lat:34.3},
  AR:{lon:-92.3,lat:34.9}, CA:{lon:-119.5,lat:37.3}, CO:{lon:-105.5,lat:39.0},
  CT:{lon:-72.7,lat:41.6}, DC:{lon:-77.0,lat:38.9}, DE:{lon:-75.5,lat:39.1},
  FL:{lon:-81.5,lat:27.8}, GA:{lon:-83.4,lat:32.6}, HI:{lon:-156.5,lat:20.5},
  IA:{lon:-93.5,lat:42.0}, ID:{lon:-114.4,lat:44.2}, IL:{lon:-89.4,lat:40.0},
  IN:{lon:-86.1,lat:40.0}, KS:{lon:-98.4,lat:38.5}, KY:{lon:-84.7,lat:37.5},
  LA:{lon:-92.3,lat:31.1}, MA:{lon:-71.8,lat:42.3}, MD:{lon:-76.7,lat:39.0},
  ME:{lon:-69.1,lat:45.3}, MI:{lon:-84.7,lat:44.3}, MN:{lon:-94.2,lat:46.3},
  MO:{lon:-92.5,lat:38.5}, MS:{lon:-89.7,lat:32.7}, MT:{lon:-110.0,lat:46.9},
  NC:{lon:-79.0,lat:35.5}, ND:{lon:-100.5,lat:47.5}, NE:{lon:-99.7,lat:41.5},
  NH:{lon:-71.6,lat:43.8}, NJ:{lon:-74.7,lat:40.1}, NM:{lon:-106.1,lat:34.5},
  NV:{lon:-116.4,lat:39.3}, NY:{lon:-75.5,lat:42.9}, OH:{lon:-82.8,lat:40.3},
  OK:{lon:-97.5,lat:35.6}, OR:{lon:-120.5,lat:44.0}, PA:{lon:-77.7,lat:40.9},
  RI:{lon:-71.5,lat:41.7}, SC:{lon:-80.9,lat:33.8}, SD:{lon:-100.0,lat:44.5},
  TN:{lon:-86.6,lat:35.8}, TX:{lon:-99.3,lat:31.0}, UT:{lon:-111.7,lat:39.4},
  VA:{lon:-78.5,lat:37.5}, VT:{lon:-72.7,lat:44.0}, WA:{lon:-120.0,lat:47.4},
  WI:{lon:-89.9,lat:44.5}, WV:{lon:-80.6,lat:38.6}, WY:{lon:-107.5,lat:43.0}
};

// Tiny states get slightly smaller label so it fits better
const SMALL_STATES = new Set(['RI','DE','DC','CT','NJ','MA','NH','VT','MD']);

const TrumpApprovalMap = ({ stateData, onStateClick, selectedState, sidebarOpen, language }) => {
  const t = useTranslation(language);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [plotRevision, setPlotRevision] = useState(0);
  const [focusState, setFocusState] = useState(null); // clicked state for zoom/focus
  const containerRef = useRef(null);

  // Keep map responsive when sidebar toggles
  useEffect(() => {
    const id = setTimeout(() => {
      setPlotRevision(p => p + 1);
      setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    }, 300);
    return () => clearTimeout(id);
  }, [sidebarOpen]);

  // Pointer cursor on hover
  const handleHover = useCallback(() => {
    if (containerRef.current) containerRef.current.style.cursor = 'pointer';
  }, []);
  const handleUnhover = useCallback(() => {
    if (containerRef.current) containerRef.current.style.cursor = 'default';
  }, []);

  // Click -> focus / toggle reset (hover tooltips remain intact on full map only)
  const handleClick = useCallback((e) => {
    const code = e?.points?.[0]?.location;
    if (!code) return;
    setFocusState(prev => (prev === code ? null : code));
    onStateClick?.(code);
  }, [onStateClick]);

  // Our own reset (used by dbl-click + button)
  const resetView = useCallback(() => setFocusState(null), []);
  const handleDomDblClick = useCallback(() => setFocusState(null), []);

  if (!stateData || stateData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>{t('noDataAvailable')}</Typography>
      </Box>
    );
  }

  try {
    const states = stateData.map(d => d.state);
    const values = stateData.map(d => d.state_TrumpJA || 0);

    // Hover text (full map only)
    const text = stateData.map(d => {
      const a = (d.state_TrumpJA || 0) * 100;
      const dis = (d.state_TrumpDisapproval || 0) * 100;
      const n = (d.state_TrumpNoOpinion || 0) * 100;
      return `<b>${d.state}</b><br>${t('trumpApprovalHover')}: ${a.toFixed(1)}%<br>Disapproval: ${dis.toFixed(1)}%<br>No Opinion: ${n.toFixed(1)}%`;
    });

    const colorScale = [
      [0,   '#0D47A1'],
      [0.33,'#42A5F5'],
      [0.66,'#EF5350'],
      [1,   '#C62828']
    ];

    let data = [];

    if (focusState) {
      const others = states.filter(s => s !== focusState);
      const focusIdx = states.indexOf(focusState);

      // 1) Other states in gray (no hover)
      data.push({
        type: 'choropleth',
        locationmode: 'USA-states',
        locations: others,
        z: others.map(() => 0),
        zmin: 0, zmax: 1,
        colorscale: [[0,'#D1D5DB'], [1,'#D1D5DB']],
        showscale: false,
        hoverinfo: 'skip',
        marker: { line: { color: isDarkMode ? '#4a5568' : '#e5e7eb', width: 0.8 }, opacity: 0.85 }
      });

      // 2) Selected state in color  HOVER DISABLED while zoomed
      data.push({
        type: 'choropleth',
        locationmode: 'USA-states',
        locations: [focusState],
        z: [values[focusIdx] ?? 0],
        zmin: 0, zmax: 1,
        colorscale: colorScale,
        hoverinfo: 'skip', // important: disable hover in focused view
        showscale: true,
        marker: { line: { color: isDarkMode ? '#ffd700' : '#111111', width: 2.5 }, opacity: 1 }
      });

      // 3) Persistent multi-line label INSIDE the focused state (stack vertically)
      const row = stateData[focusIdx];
      const c = STATE_CENTROIDS[focusState];
      if (row && c) {
        const a = ((row.state_TrumpJA || 0) * 100).toFixed(1);
        const d = ((row.state_TrumpDisapproval || 0) * 100).toFixed(1);
        const n = ((row.state_TrumpNoOpinion || 0) * 100).toFixed(1);
        const labelSize = SMALL_STATES.has(focusState) ? 14 : 16;

        data.push({
          type: 'scattergeo',
          mode: 'text',
          lon: [c.lon],
          lat: [c.lat],
          text: [
            `<b>${focusState}</b><br>` +
            `Approval: ${a}%<br>` +
            `Disapproval: ${d}%<br>` +
            `No Opinion: ${n}%`
          ],
          textposition: 'middle center',
          textfont: {
            family: '"Poppins", sans-serif',
            size: labelSize,
            color: '#111827'
          },
          hoverinfo: 'skip',
          showlegend: false
        });
      }
    } else {
      // Full USA normal view (hover tooltips enabled)
      data.push({
        type: 'choropleth',
        locationmode: 'USA-states',
        locations: states,
        z: values,
        zmin: 0, zmax: 1,
        colorscale: colorScale,
        text,
        hovertemplate: '%{text}<extra></extra>',
        marker: {
          line: {
            color: selectedState
              ? states.map(s => s === selectedState ? (isDarkMode ? '#ffd700' : '#000') : (isDarkMode ? '#4a5568' : '#ffffff'))
              : (isDarkMode ? '#4a5568' : '#ffffff'),
            width: selectedState ? states.map(s => s === selectedState ? 3 : 0.8) : 0.8
          },
          opacity: 0.95
        }
      });
    }

    // Zoom / center
    const defaultCenter = { lon: -97, lat: 39 };
    const center = focusState ? (STATE_CENTROIDS[focusState] || defaultCenter) : defaultCenter;
    const scale  = focusState ? 4.2 : 1.05;

    const layout = {
      geo: {
        scope: 'usa',
        projection: { type: 'albers usa', scale },
        center,
        showlakes: true,
        lakecolor: isDarkMode ? '#1a202c' : 'rgb(255,255,255)',
        bgcolor: isDarkMode ? '#414141' : '#ffffff',
        coastlinecolor: isDarkMode ? '#4a5568' : '#000000',
        landcolor: isDarkMode ? '#f3f4f6' : '#f3f4f6',
        showland: true,
        showocean: true,
        oceancolor: isDarkMode ? '#1a202c' : '#ffffff'
      },
      margin: { l: 0, r: 80, t: 20, b: 0 },
      autosize: true,
      font: { family: '"Poppins", sans-serif', size: 12, color: isDarkMode ? '#e2e8f0' : '#374151' },
      datarevision: plotRevision,
      plot_bgcolor: isDarkMode ? '#414141' : '#ffffff',
      paper_bgcolor: isDarkMode ? '#414141' : '#ffffff',
      hovermode: 'closest',
      hoverlabel: {
        bgcolor: isDarkMode ? '#374151' : '#ffffff',
        bordercolor: isDarkMode ? '#4b5563' : '#e5e7eb',
        font: { color: isDarkMode ? '#f3f4f6' : '#111827' }
      },

      // HARD DISABLE any hidden range sliders / templates to avoid _rangeSliderData errors
      template: null,
      xaxis:   { visible: false, showgrid: false, zeroline: false, rangeslider: { visible: false } },
      yaxis:   { visible: false, showgrid: false, zeroline: false, rangeslider: { visible: false } },
      xaxis2:  { rangeslider: { visible: false } },
      yaxis2:  { rangeslider: { visible: false } },
      xaxis3:  { rangeslider: { visible: false } },
      yaxis3:  { rangeslider: { visible: false } },

      // Smooth zoom transition
      transition: { duration: 200, easing: 'cubic-in-out' }
    };

    const config = {
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['pan2d','lasso2d','select2d','autoScale2d','resetScale2d'],
      responsive: true,
      toImageButtonOptions: { format: 'png', filename: 'electoral-intelligence-map', height: 600, width: 1000, scale: 2 },
      scrollZoom: false,

      // IMPORTANT: disable Plotlys own double-click behavior (we handle it ourselves)
      doubleClick: false,

      showTips: true,
      plotGlPixelRatio: 2
    };

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 500 }}>
        {/* Reset button appears only when focused */}
        {focusState && (
          <Tooltip title="Reset map">
            <IconButton
              size="small"
              onClick={resetView}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: 'background.paper' }}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box
          ref={containerRef}
          onDoubleClick={handleDomDblClick} // our own dbl-click reset
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            '& .js-plotly-plot': { width: '100% !important', height: '100% !important' }
          }}
        >
          <Plot
            data={data}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
            key={`plot-${plotRevision}-${focusState || 'all'}`}
            onClick={handleClick}
            onHover={handleHover}
            onUnhover={handleUnhover}
            // DO NOT pass Plotly's onDoubleClick; we handle dbclick in the wrapper Box
          />
        </Box>
      </Box>
    );
  } catch (err) {
    console.error('Map error:', err);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <Typography variant="body1" color="error">
          Error loading map. Please try again.
        </Typography>
      </Box>
    );
  }
};

export default TrumpApprovalMap;



